import { useEffect, useRef, useState } from "react";

// Adds `is-visible` when the element scrolls into view (one-shot).
export function useReveal(options = {}) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px", ...options }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [options]);
  return ref;
}

// Virtual, self-looping countdown: counts down from J-`cycleDays` to 0,
// then automatically loops back to the start. No date, no backend, no
// storage — perfect for a static build embedded on a site.
export function useCountdown(cycleDays = 7) {
  const [remaining, setRemaining] = useState({ d: 0, h: 0, m: 0, s: 0 });

  useEffect(() => {
    const days =
      (typeof window !== "undefined" && Number(window.__PROMO_DAYS__)) || cycleDays;
    const CYCLE = Math.max(1, days) * 86400000;
    const tick = () => {
      const diff = CYCLE - (Date.now() % CYCLE); // ~CYCLE -> 0, then loops
      setRemaining({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [cycleDays]);

  return remaining;
}

export function scrollToId(id) {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}
